import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from './client';

export type ExternalCalendar = Schemas['ExternalCalendarDto'];
export type ExternalEvent = Schemas['ExternalEvent'];

const calKey = (ws: string) => ['workspace', ws, 'external-calendars'] as const;

export function useCalendarFeed(ws: string) {
  return useQuery({
    queryKey: ['workspace', ws, 'calendar-feed'],
    queryFn: () => unwrap(api.GET('/api/v1/workspaces/{workspaceId}/calendar-feed', { params: { path: { workspaceId: ws } } })),
  });
}

export function useCalendarFeedAction(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (action: 'create' | 'delete') =>
      action === 'create'
        ? unwrap(api.POST('/api/v1/workspaces/{workspaceId}/calendar-feed', { params: { path: { workspaceId: ws } } }))
        : unwrap(api.DELETE('/api/v1/workspaces/{workspaceId}/calendar-feed', { params: { path: { workspaceId: ws } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workspace', ws, 'calendar-feed'] }),
  });
}

export function useExternalCalendars(ws: string) {
  return useQuery({
    queryKey: calKey(ws),
    queryFn: () =>
      unwrap(api.GET('/api/v1/workspaces/{workspaceId}/external-calendars', { params: { path: { workspaceId: ws } } })),
  });
}

export function useSaveExternalCalendar(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Schemas['ExternalCalendarRequest'] & { id?: string }) =>
      id
        ? unwrap(
            api.PATCH('/api/v1/workspaces/{workspaceId}/external-calendars/{id}', { params: { path: { workspaceId: ws, id } }, body }),
          )
        : unwrap(api.POST('/api/v1/workspaces/{workspaceId}/external-calendars', { params: { path: { workspaceId: ws } }, body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: calKey(ws) }),
  });
}

export function useDeleteExternalCalendar(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE('/api/v1/workspaces/{workspaceId}/external-calendars/{id}', { params: { path: { workspaceId: ws, id } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: calKey(ws) }),
  });
}

export function useExternalEvents(ws: string, from: string, to: string, enabled: boolean) {
  return useQuery({
    queryKey: [...calKey(ws), 'events', from, to],
    queryFn: () =>
      unwrap(
        api.GET('/api/v1/workspaces/{workspaceId}/external-calendars/events', {
          params: { path: { workspaceId: ws }, query: { from, to } },
        }),
      ),
    enabled,
    staleTime: 5 * 60_000,
  });
}
